import { ref } from "vue";
import { DEFAULT_INTERMEDIATE_ORDER_PO_CUBE } from "../../features/intermediateOrders/intermediateOrderRedistribution.ts";
import type {
  BulkOrderAllocationRow,
  IntermediateOrderSearchDiagnostics,
} from "../../redistribution/models/IntermediateOrderSearchResult.ts";
import type { IntermediateOrderSkuRow } from "../../redistribution/models/IntermediateOrderSkuRow.ts";

export function createIntermediateOrderResultsModule() {
  // undefined = no search has run yet; [] = a search ran and found nothing.
  const intermediateOrderSearchResults = ref<
    IntermediateOrderSkuRow[] | undefined
  >(undefined);
  const intermediateOrderSearching = ref(false);
  // Set when a search/refresh fails; distinct from "ran and found nothing" so
  // the results panel can tell the two apart. Cleared on the next successful search.
  const intermediateOrderSearchError = ref<string | undefined>(undefined);
  // The last query searched, so a "Refresh" action can re-run it without the
  // user having to retype it.
  const intermediateOrderLastQuery = ref<string | undefined>(undefined);
  const intermediateOrderPoCube = ref<string>(
    String(DEFAULT_INTERMEDIATE_ORDER_PO_CUBE)
  );
  // View-only Bulk Order allocations returned when no Intermediate PO rows exist.
  const intermediateOrderBulkAllocations = ref<BulkOrderAllocationRow[]>([]);
  // Why the last search returned no Intermediate PO rows; undefined when rows were found.
  const intermediateOrderSearchDiagnostics = ref<
    IntermediateOrderSearchDiagnostics | undefined
  >(undefined);

  function setIntermediateOrderSearchResults(
    rows: IntermediateOrderSkuRow[],
    bulkAllocations: BulkOrderAllocationRow[] = [],
    diagnostics?: IntermediateOrderSearchDiagnostics
  ): void {
    intermediateOrderSearchResults.value = rows;
    intermediateOrderBulkAllocations.value = bulkAllocations;
    intermediateOrderSearchDiagnostics.value = diagnostics;
  }

  function setIntermediateOrderSearching(value: boolean): void {
    intermediateOrderSearching.value = value;
  }

  function setIntermediateOrderSearchError(message: string | undefined): void {
    intermediateOrderSearchError.value = message;
  }

  function setIntermediateOrderLastQuery(query: string | undefined): void {
    intermediateOrderLastQuery.value = query;
  }

  function clearIntermediateOrderSearchResults(): void {
    intermediateOrderSearchResults.value = undefined;
    intermediateOrderSearchError.value = undefined;
    intermediateOrderBulkAllocations.value = [];
    intermediateOrderSearchDiagnostics.value = undefined;
  }

  return {
    intermediateOrderSearchResults,
    intermediateOrderSearching,
    intermediateOrderSearchError,
    intermediateOrderLastQuery,
    intermediateOrderPoCube,
    intermediateOrderBulkAllocations,
    intermediateOrderSearchDiagnostics,
    setIntermediateOrderSearchResults,
    setIntermediateOrderSearching,
    setIntermediateOrderSearchError,
    setIntermediateOrderLastQuery,
    clearIntermediateOrderSearchResults,
  };
}
